/**
 * kanzleiAccess — fail-closed FE-Sichtbarkeits-Resolver für den entkoppelten
 * Steuerberater-/Kanzlei-Funktionsblock (M-GA-3, `architecture-stb-kanzlei-ga.md`
 * §4.1–§4.4).
 *
 * Zerlegt das bisher monolithische `isLoopBBeta`-Gate in einzeln flippbare
 * Feature-Gates nach der invarianten Formel `GA_FLAG || isLoopBBeta(uid, sellerData)`.
 * Eigenschaften (§4.1):
 *   - Alle Flags absent ⇒ jeder Resolver ist byte-äquivalent zu `isLoopBBeta`
 *     ⇒ Merge ist INERT (kein Verhaltens-Delta, kein Feature-Verlust der Beta-Kohorte).
 *   - Jeder GA-Flip ist genau ein Flag; fail-closed (nur exakt `'true'` aktiviert).
 *   - Rein, synchron, Firebase-frei ⇒ importierbar aus `config/navigation.ts`
 *     (Muster `advisorLandingAccess.ts` / `messagingAccess.ts`).
 *
 * Sicherheitshinweis: rein KOSMETISCHES Client-Gating — KEIN Zugriffsschutz. Die
 * harte, fail-closed Sperre bleibt server-seitig
 * (`functions/src/access/kanzleiGates.ts` bzw. `loopBBetaAccess.ts`) und in den
 * Firestore-Rules. Ein client-gefälschtes `loopBBeta` hilft nichts (backend-only
 * Feld, `saveSellerData` strippt es, Rules verbieten den Client-Write).
 *
 * @module config/kanzleiAccess
 */

import { isLoopBBeta } from './loopBBetaAccess';

/**
 * Minimales `sellerData`-Shape, das die Resolver lesen — nur das `loopBBeta`-Feld.
 * `null`/`undefined` (noch nicht geladen) ⇒ fail-closed über `isLoopBBeta`.
 */
export type LoopBSellerData = { loopBBeta?: boolean } | null | undefined;

/**
 * GA-Flags je Feature. Modul-Konstanten, EINMALIG beim Bundle-Load ausgewertet
 * (Muster `MESSAGING_ENABLED` / `UX_COCKPIT_ENABLED`). Fail-closed: nur exakt
 * `'true'` aktiviert — fehlend/leer/`'false'`/`'TRUE'` ⇒ `false`.
 */

/** Cluster A — Owner→StB-Einladung (`REACT_APP_KANZLEI_INVITE_GA`). */
export const KANZLEI_INVITE_GA: boolean = process.env.REACT_APP_KANZLEI_INVITE_GA === 'true';

/** Cluster B — Kanzlei→Mandant-Reverse-Invite + Bulk (`REACT_APP_KANZLEI_CLIENT_INVITE_GA`). */
export const KANZLEI_CLIENT_INVITE_GA: boolean =
  process.env.REACT_APP_KANZLEI_CLIENT_INVITE_GA === 'true';

/** Cluster C — Kanzlei-Cockpit READ + Findings-Auflösung + Mandanten-Übersicht (`REACT_APP_KANZLEI_COCKPIT_GA`). */
export const KANZLEI_COCKPIT_GA: boolean = process.env.REACT_APP_KANZLEI_COCKPIT_GA === 'true';

/** Cluster D — Prüf-Signale (Trigger/Seal/Hints) (`REACT_APP_KANZLEI_DOC_CHECKS_GA`). */
export const KANZLEI_DOC_CHECKS_GA: boolean =
  process.env.REACT_APP_KANZLEI_DOC_CHECKS_GA === 'true';

/** Cluster E — Chat/Nachricht an Mandant (`REACT_APP_KANZLEI_MESSAGING_GA`). */
export const KANZLEI_MESSAGING_GA: boolean =
  process.env.REACT_APP_KANZLEI_MESSAGING_GA === 'true';

/** Cluster F — DATEV-Sammelexport (`REACT_APP_KANZLEI_DATEV_GA`; bleibt vorerst ungesetzt). */
export const KANZLEI_DATEV_GA: boolean = process.env.REACT_APP_KANZLEI_DATEV_GA === 'true';

/**
 * Cluster B — Reverse-Invite (Kanzlei lädt Mandant ein) + Bulk + `advisor_profiles`.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param s - eigenes `sellerData` aus `useAuth()` (nur `loopBBeta` wird gelesen).
 * @returns `true`, wenn `KANZLEI_CLIENT_INVITE_GA` aktiv ODER die Identität Beta ist.
 *          Sonst `false` (fail-closed).
 */
export function canUseClientInvite(uid: string | null | undefined, s: LoopBSellerData): boolean {
  return KANZLEI_CLIENT_INVITE_GA || isLoopBBeta(uid, s);
}

/**
 * Cluster C — Kanzlei-Cockpit READ + Findings-Auflösung + Mandanten-Übersicht.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param s - eigenes `sellerData` aus `useAuth()` (nur `loopBBeta` wird gelesen).
 * @returns `true`, wenn `KANZLEI_COCKPIT_GA` aktiv ODER die Identität Beta ist.
 *          Sonst `false` (fail-closed).
 */
export function canUseKanzleiCockpit(uid: string | null | undefined, s: LoopBSellerData): boolean {
  return KANZLEI_COCKPIT_GA || isLoopBBeta(uid, s);
}

/**
 * Cluster D — Prüf-Signale (Findings-Erzeugung, Owner-Worklist, Verified-Seal).
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param s - eigenes `sellerData` aus `useAuth()` (nur `loopBBeta` wird gelesen).
 * @returns `true`, wenn `KANZLEI_DOC_CHECKS_GA` aktiv ODER die Identität Beta ist.
 *          Sonst `false` (fail-closed).
 */
export function canUseDocChecks(uid: string | null | undefined, s: LoopBSellerData): boolean {
  return KANZLEI_DOC_CHECKS_GA || isLoopBBeta(uid, s);
}

/**
 * Cluster F — DATEV-Sammelexport (Flag bleibt vorerst ungesetzt ⇒ effektiv Beta-only).
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param s - eigenes `sellerData` aus `useAuth()` (nur `loopBBeta` wird gelesen).
 * @returns `true`, wenn `KANZLEI_DATEV_GA` aktiv ODER die Identität Beta ist.
 *          Sonst `false` (fail-closed).
 */
export function canUseAdvisorDatev(uid: string | null | undefined, s: LoopBSellerData): boolean {
  return KANZLEI_DATEV_GA || isLoopBBeta(uid, s);
}

/**
 * Cluster E — Chat/„Nachricht an Mandant" (Sichtbarkeit).
 *
 * WICHTIG (§4.4): Dieser Resolver ersetzt NUR den bisherigen Beta-Term
 * (`isLoopBBeta(uid, s)`) an der aufrufenden Komponente. Die zusätzliche
 * Scope-Kompatibilität über `canUseMessaging(scopeUid)` bleibt UNVERÄNDERT in
 * der aufrufenden Komponente (`InboundDocumentDetail`) und wird NICHT hier
 * hineingezogen — `messagingAccess.ts`/`canUseMessaging` werden nicht angefasst.
 * Additiv: der neue GA-Flag wirkt zusätzlich, die Beta-Kohorte verliert nichts.
 *
 * @param uid - Firebase-Auth-UID des aktuellen Nutzers (oder null/undefined).
 * @param s - eigenes `sellerData` aus `useAuth()` (nur `loopBBeta` wird gelesen).
 * @returns `true`, wenn `KANZLEI_MESSAGING_GA` aktiv ODER die Identität Beta ist.
 *          Sonst `false` (fail-closed).
 */
export function canUseKanzleiMessaging(uid: string | null | undefined, s: LoopBSellerData): boolean {
  return KANZLEI_MESSAGING_GA || isLoopBBeta(uid, s);
}

/**
 * W3 Self-Service-Kanzlei (M-OB-12, §8.3) — reiner, synchroner FE-Sichtbarkeits-
 * Helfer für die Registrierungs-Kennzeichnung `sellers.accountType === 'kanzlei'`.
 *
 * WICHTIG (§4 Z.107): `accountType` ist ein reines UX-Signal und **KEINE
 * Autorisierungsquelle** — es steuert ausschließlich FE-Sichtbarkeit
 * (Nav-Prädikate, AdvisorLandingGate, MandantenPage-Stepper). Der harte
 * Zugriffsschutz bleibt server-seitig (Membership + Consent) und in den
 * Firestore-Rules. Ein client-gesetztes `accountType` verschafft KEINEN Zugriff.
 *
 * Bewusst `unknown` als Parameter: `NavContext.sellerData` ist `unknown`
 * (Config-Entkopplung vom `Seller`-Typ), und der echte `Seller` ist
 * strukturell zuweisbar an `unknown` — derselbe Helfer bedient also sowohl die
 * Nav-Prädikate als auch die typisierten Aufrufer (MandantenPage/LandingGate).
 * Null-/Typ-sicher: fehlendes/nicht-objekt `sellerData` ⇒ `false` (fail-closed).
 *
 * @param sellerData - eigenes `sellerData` aus `useAuth()` (oder null/unknown).
 * @returns `true`, wenn `accountType` exakt `'kanzlei'` ist, sonst `false`.
 */
export function isKanzleiAccount(sellerData: unknown): boolean {
  if (sellerData === null || sellerData === undefined || typeof sellerData !== 'object') {
    return false;
  }
  return (sellerData as { accountType?: unknown }).accountType === 'kanzlei';
}
